/**
 * LYT Communications - Online Status Hook
 *
 * Combines browser navigator.onLine events with an API ping
 * to detect actual server reachability (not just WiFi connected).
 * Re-pings every 15s when browser says online but API was unreachable.
 */

import { useState, useEffect, useCallback, useRef } from 'react';

export default function useOnlineStatus() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const pingTimer = useRef(null);

  const pingApi = useCallback(async () => {
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 5000);
      const resp = await fetch('/api/projects?project_id=_ping', {
        signal: controller.signal,
      });
      clearTimeout(timeout);
      // Any response (even 404) means server is reachable
      setIsOnline(resp.ok || resp.status < 500);
      return true;
    } catch {
      setIsOnline(false);
      return false;
    }
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      // Browser says online — verify with API ping
      pingApi();
    };
    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [pingApi]);

  // Re-ping every 15s when browser says online but we think we're offline
  useEffect(() => {
    if (pingTimer.current) clearInterval(pingTimer.current);

    if (navigator.onLine && !isOnline) {
      pingTimer.current = setInterval(() => {
        pingApi();
      }, 15000);
    }

    return () => {
      if (pingTimer.current) clearInterval(pingTimer.current);
    };
  }, [isOnline, pingApi]);

  return { isOnline, pingApi };
}
